/**
 * Formatting utility functions for currency, dates, and other display values
 */

/**
 * Format a number as USD currency
 * @param {number|string} amount - Amount in dollars (e.g., 35.99 or "35.99")
 * @returns {string} - Formatted currency string (e.g., "$35.99")
 */
export const formatCurrency = (amount) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

// Parse a date string as local time. YYYY-MM-DD strings are spec-parsed as UTC
// by new Date(), which shifts the date back one day in negative-offset timezones.
export const parseDate = (dateString) => {
  if (typeof dateString !== 'string') return dateString;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  if (dateOnly) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateString);
};

/**
 * Format a date string as a readable date
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} - Formatted date (e.g., "January 15, 2025")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = parseDate(dateString);

    if (isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format a date string as a readable date and time
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} - Formatted date and time (e.g., "January 15, 2025 at 2:30 PM")
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';

  try {
    const date = parseDate(dateString);

    if (isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '';
  }
};

/**
 * Format a date string as a short date for inputs (YYYY-MM-DD)
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} - Formatted date for date input (e.g., "2025-01-15")
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  try {
    const date = parseDate(dateString);

    if (isNaN(date.getTime())) {
      return '';
    }

    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

/**
 * Format a phone number as the user types: 555-555-5555
 * @param {string} phone - Raw input value
 * @returns {string} - Partially or fully formatted phone string
 */
export const formatPhoneInput = (phone) => {
  if (!phone) return '';
  let digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Format a phone number for display
 * @param {string} phone - Phone number string
 * @returns {string} - Formatted phone (e.g., "(555) 123-4567")
 */
export const formatPhone = (phone) => {
  if (!phone) return '';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // If not 10 digits, return as-is
  return phone;
};

/**
 * Format order number for display
 * @param {string} orderNumber - Order number (e.g., "DRM-2025-0001")
 * @returns {string} - Formatted order number with proper spacing
 */
export const formatOrderNumber = (orderNumber) => {
  if (!orderNumber) return '';
  return orderNumber;
};

/**
 * Format an integer cents value as USD currency
 * @param {number} cents - Amount in cents (e.g., 3599 → "$35.99")
 * @returns {string} - Formatted currency string
 */
export const formatCents = (cents) => {
  return formatCurrency((cents ?? 0) / 100);
};

/**
 * Truncate text to a maximum length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};
